import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import Note from "../../Assets/images/note.svg";

// Format date helper
const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

function ActiveInvestmentUpdatesTab({
  updates = [],
  loadingUpdates = false,
  error = false,
}) {
  // Loading state
  if (loadingUpdates) {
    return (
      <div role="status" className="flex justify-center my-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00BC67]" />
        <span className="sr-only">Loading updates...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        role="alert"
        className="bg-white rounded-[16px] border border-dashed border-gray-200 p-8 text-center my-4"
      >
        <h4 className="text-[#1E222B] font-[700] text-[14px] mb-1">
          Unable to Load Updates
        </h4>
        <p className="text-[#808285] text-[12px]">
          Something went wrong while fetching updates. Please try again later.
        </p>
      </div>
    );
  }

  // Empty state
  if (!updates || updates.length === 0) {
    return (
      <div className="bg-white rounded-[16px] border border-dashed border-gray-200 p-8 text-center flex flex-col items-center justify-center my-4">
        <img
          src={Note}
          alt="No updates"
          className="w-14 h-14 opacity-50 mb-3"
        />
        <h4 className="text-[#1E222B] font-[700] text-[14px] mb-1">
          No Updates Yet
        </h4>
        <p className="text-[#808285] text-[12px] max-w-xs leading-relaxed">
          Project progress updates will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="py-2">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[#1E222B] text-[13px] font-[800]">
          Project Progress
        </h3>
      </div>

      <div className="relative pl-0.5 mt-2">
        {updates.map((stage, idx) => {
          const isLast = idx === updates.length - 1;
          const isCompleted =
            stage.status === "completed" || stage.isCompleted;

          const title = stage.phaseName || stage.title || "Milestone";
          const description = stage.phaseDescription || stage.description;
          const date = stage.createdAt || stage.date;
          const images = stage.phaseImages || stage.images || [];

          return (
            <div
              key={stage.id || idx}
              className={`relative flex items-start gap-3 ${
                !isLast ? "pb-7" : ""
              }`}
            >
              {!isLast && (
                <div
                  className="absolute left-[10.5px] top-[22px] bottom-0 w-[1.5px] bg-[#94A3B8]/35"
                  aria-hidden="true"
                />
              )}
              {isCompleted ? (
                <div className="relative z-10 w-[22px] h-[22px] rounded-full bg-[#00BC67] flex items-center justify-center flex-shrink-0 shadow-xs">
                  <CheckIcon
                    sx={{
                      fontSize: 13,
                      color: "#FFFFFF",
                      stroke: "#FFFFFF",
                      strokeWidth: 0.5,
                    }}
                  />
                </div>
              ) : (
                <div className="relative z-10 w-[22px] h-[22px] rounded-full bg-[#A3E7CA] flex items-center justify-center flex-shrink-0" />
              )}
              <div className="flex-1 -mt-0.5">
                <div className="flex items-center justify-between">
                  <h4 className="text-[#1E222B] text-[13px] font-[700]">
                    {title}
                  </h4>
                  {date && (
                    <span className="text-[#808285] text-[11px] font-[400]">
                      {formatDate(date)}
                    </span>
                  )}
                </div>
                {description && (
                  <p className="text-[#808285] text-[11.5px] font-[400] mt-0.5 leading-snug">
                    {description}
                  </p>
                )}
                {Array.isArray(images) && images.length > 0 && (
                  <div className="flex items-center gap-2 mt-2.5">
                    {images.map((img, imgIdx) => (
                      <div
                        key={imgIdx}
                        className="w-[66px] h-[66px] rounded-[8px] overflow-hidden bg-[#F1F5F9] border border-black/5"
                      >
                        <img
                          src={img?.imageUrl || img}
                          alt={`Progress ${imgIdx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ActiveInvestmentUpdatesTab;
